import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePlatform } from "../context/PlatformContext";
import { useBamNoIndex } from "../lib/useSeo";

/**
 * BamPublicGate — wraps a marketplace-flavored public page with two layers
 * of Bank Approval Mode hardening:
 *
 *  1. Always applies `useBamNoIndex(bam)` so the page emits
 *     `<meta name="robots" content="noindex,nofollow">` while BAM is ON, and
 *     reverts to default indexing the moment BAM flips OFF.
 *  2. Optionally `redirectWhenBam` — sends public visitors and collectors to
 *     `/about/catalog` while leaving admin + vendor accounts on the page so
 *     internal workflows are never blocked.
 *
 * Usage:
 *   export default function ApplyBreeder() {
 *     return (
 *       <BamPublicGate redirectWhenBam>
 *         <ApplyBreederBody/>
 *       </BamPublicGate>
 *     );
 *   }
 */
export default function BamPublicGate({ children, redirectWhenBam = false, redirectTo = "/about/catalog" }) {
  const { bank_approval_mode, loaded } = usePlatform();
  const { user } = useAuth();
  useBamNoIndex(bank_approval_mode);
  // user === null while /auth/me is still in flight. Wait for auth + BAM
  // settings to both resolve before deciding to redirect, otherwise admins
  // and vendors get bounced to /about/catalog on direct navigation before
  // the auth context has identified them as internal.
  const authLoaded = user !== null;
  const isInternal = user && (user.role === "admin" || user.role === "vendor");
  if (loaded && authLoaded && bank_approval_mode && redirectWhenBam && !isInternal) {
    return <Navigate to={redirectTo} replace/>;
  }
  return children;
}
